import { withAlpha, hex, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface FloatOptions {
  /** Newlines break it into lines, drawn centered under one another. */
  text: string;
  /** Where it starts, in the layer's own coordinates. */
  x: number;
  y: number;
  color?: Color | null;
  font?: FontSpec | null;
  /** How long it lives, in seconds. */
  seconds?: number;
  /** Pixels per second it drifts. Negative y is upward. */
  driftX?: number;
  driftY?: number;
  /** Scale at birth, easing to 1. A crit pops bigger. */
  pop?: number;
  /** Wrap at words to lines no wider than this many pixels. Unset, only newlines break. */
  wrapWidth?: number;
  /**
   * Which part of the text sits at `y`. `middle` centers the block on it, as a
   * damage number wants; `bottom` puts the last line there and grows upward,
   * as speech over a head does.
   */
  anchor?: FloatAnchor;
}

export type FloatAnchor = "middle" | "bottom";

export interface FloatingTextOptions extends ControlOptions {
  /** Default lifetime for floats that do not name one. */
  seconds?: number;
  driftX?: number;
  driftY?: number;
  font?: FontSpec | null;
  /** Oldest floats are dropped past this many, so a burst cannot flood. */
  maxFloats?: number;
}

interface Float {
  text: string;
  x: number;
  y: number;
  color: Color | null;
  font: FontSpec | null;
  bornAt: number;
  seconds: number;
  driftX: number;
  driftY: number;
  pop: number;
  wrapWidth: number | null;
  anchor: FloatAnchor;
  /** Wrapped on first paint, when there is a font to measure with, and kept. */
  lines: string[] | null;
}

const DEFAULT_SECONDS = 1.1;
const DEFAULT_DRIFT_Y = -46;
const DEFAULT_MAX = 64;
/** Last part of a float's life spent fading out. */
const FADE_PORTION = 0.35;

/** Damage red and heal green, in the shades the genre uses. */
export const DAMAGE_COLOR = hex("#e04b4b");
export const HEAL_COLOR = hex("#4fc26b");
export const CRIT_COLOR = hex("#ffcc44");

/**
 * Damage numbers, pickup notices, "+50 XP" — text that appears at a point,
 * drifts, fades and takes itself away.
 *
 * One control for the whole lot rather than one per number. Fifty hits in a
 * second is an ordinary thing for a game to do, and fifty controls entering
 * and leaving the tree every second is not: each one is a layout, a hit-test
 * entry and an animator registration. This holds them as plain records,
 * registers once, and draws them in a loop.
 *
 * Positions are in the layer's own coordinates, so a caller that has a world
 * point projects it first — the same thing `Nameplate` does.
 */
export class FloatingText extends Control {
  #floats: Float[] = [];
  #animating = false;

  seconds: number;
  driftX: number;
  driftY: number;
  font: FontSpec | null;
  maxFloats: number;

  constructor(options: FloatingTextOptions = {}) {
    super({ width: 400, height: 300, tabStop: false, ...options });
    this.seconds = options.seconds ?? DEFAULT_SECONDS;
    this.driftX = options.driftX ?? 0;
    this.driftY = options.driftY ?? DEFAULT_DRIFT_Y;
    this.font = options.font ?? null;
    this.maxFloats = Math.max(1, options.maxFloats ?? DEFAULT_MAX);
  }

  /** Floats currently in the air. */
  get count(): number {
    return this.#floats.length;
  }

  /** A layer of drifting text answers no hit test; it is pure decoration. */
  protected override hitTestSelf(): boolean {
    return false;
  }

  /** Throw one piece of text into the air. */
  spawn(options: FloatOptions): void {
    this.#floats.push({
      text: options.text,
      x: options.x,
      y: options.y,
      color: options.color ?? null,
      font: options.font ?? null,
      bornAt: this.desktop?.frameTime ?? 0,
      seconds: options.seconds ?? this.seconds,
      driftX: options.driftX ?? this.driftX,
      driftY: options.driftY ?? this.driftY,
      pop: options.pop ?? 1,
      wrapWidth: options.wrapWidth ?? null,
      anchor: options.anchor ?? "middle",
      lines: null,
    });
    // Oldest out first: a burst should leave the newest readable rather than
    // the screen full of history.
    while (this.#floats.length > this.maxFloats) this.#floats.shift();
    this.#syncAnimation();
    this.invalidate();
  }

  /** A damage number, in the usual red, or gold and bigger for a crit. */
  damage(amount: number, x: number, y: number, crit = false): void {
    this.spawn({
      text: crit ? `${amount}!` : String(amount),
      x,
      y,
      color: crit ? CRIT_COLOR : DAMAGE_COLOR,
      pop: crit ? 1.6 : 1,
    });
  }

  /** A heal, in green and with a plus. */
  heal(amount: number, x: number, y: number): void {
    this.spawn({ text: `+${amount}`, x, y, color: HEAL_COLOR });
  }

  clear(): void {
    this.#floats.length = 0;
    this.#syncAnimation();
    this.invalidate();
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#floats.length > 0;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    this.#syncAnimation();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const now = this.desktop?.frameTime ?? 0;
    const baseFont = this.font ?? theme.metrics.captionFont;

    // Reap and draw in one pass, backwards so a removal does not skip the
    // next float along.
    for (let i = this.#floats.length - 1; i >= 0; i--) {
      const f = this.#floats[i]!;
      const age = now - f.bornAt;
      if (age >= f.seconds) {
        this.#floats.splice(i, 1);
        continue;
      }
      const t = f.seconds > 0 ? age / f.seconds : 1;
      // Ease out: fast at first, drifting to a stop, which reads as impact.
      const eased = 1 - (1 - t) * (1 - t);
      const alpha = t < 1 - FADE_PORTION ? 1 : (1 - t) / FADE_PORTION;

      const font = f.font ?? baseFont;
      // The pop is a size, and the atlas rasterises per size, so it is
      // quantised to a few steps rather than a new raster every frame.
      const scale = f.pop > 1 ? 1 + (f.pop - 1) * (1 - eased) : 1;
      const sized: FontSpec =
        scale > 1.02 ? { ...font, size: Math.round(font.size * scale * 2) / 2 } : font;

      const ink = withAlpha(f.color ?? theme.palette.text, Math.max(0, Math.min(1, alpha)));
      // Wrapped at the unscaled size, so a popping float keeps its line breaks
      // as it settles rather than reflowing mid-flight.
      f.lines ??= f.wrapWidth != null ? rc.wrapText(f.text, font, f.wrapWidth) : f.text.split("\n");
      const step = rc.lineHeight(sized);
      const x = f.x + f.driftX * eased;
      const last = f.lines.length - 1;
      const first = f.y + f.driftY * eased - (f.anchor === "bottom" ? last : last / 2) * step;
      for (let n = 0; n <= last; n++) {
        const line = f.lines[n]!;
        if (!line) continue;
        const width = rc.measureText(line, sized).width;
        theme.drawFloatingText(rc, line, x - width / 2, first + n * step, sized, ink);
      }
    }
    this.#syncAnimation();
  }
}
